/**
 * 通知的**事实**与**浮层标题**——「明确的标题 + 具体的内容」的实现（ADR 0010）。
 *
 * ## 事实是什么
 *
 * 一条通知要能回答三个问题，缺一个浮层就只能说「出事了」：
 *
 * | 问题 | 槽位 | 载体上的键 |
 * |---|---|---|
 * | **谁 / 哪个** | 主体账号名 | `subject` |
 * | | 关联订单号 | `order_no` |
 * | **哪一档** | 登录阶段（30 邮箱码 / 32 令牌码） | `stage` |
 * | | 动作类型（哪类操作） | `action` |
 * | **什么码** | 具体失败码 | `code` |
 *
 * ⚠️ **事实与路由是两回事**：铃铛帧的收件人解析键（后端 `username`）**不进线**
 * ⇒ 主体走 `subject` ⇒ 前端读 `subject` 不会碰到收件人语义。
 *
 * ⚠️ **两条路只保证「事实」一致**：实时浮层吃铃铛帧，补弹浮层吃行读面 ⇒
 * 两者的事实**同源** ⇒ 标题与分档一致；⚠️ **协议细节**（原始 Steam 码 /
 * 失败步骤）只在实时那条里（行读面不给载荷快照）。
 *
 * ## 标题怎么来
 *
 * **标题 = 事件名（帧 + 分档），无插值**（用户裁定 2026-10-24：标题说
 * 「什么类型的事件」，主角进正文）：
 *
 * | 来源帧 | 分档 | 标题 |
 * |---|---|---|
 * | `account.need_code` | 阶段 30 / 32 | **等待邮箱 / 令牌**验证码 |
 * | `account.failed` | 动作（登录 / 重登 / 验证 / 令牌刷新 / 保活） | **保活**失败 |
 * | `cdkey.bound` | — | CDKey 已绑定 |
 * | `fx.refresh_failed` | — | 汇率刷新失败 |
 *
 * ⇒ 标题**不依赖主体**（有没有账号名，标题都成立）⇒ 旧设计里
 * 「主体未知 ⇒ 回落类别标题」的整条分支不存在了。
 *
 * ## 分档（grade）进聚合键
 *
 * 分档就是**事件的细分**（等邮箱码与等令牌码是两个事件）⇒ 键带分档
 * （`features/events/notices.ts::noticeKey`）；主体不在键里（一格可装
 * 多个主角，见该处的口径）。
 */

import { frameKey } from './notificationFrames';

/** 五个事实槽位——缺哪个就是 `null`（**不猜、不编**）。 */
export interface NoticeFacts {
  /** 主体账号名（谁）。 */
  subject: string | null;
  /** 关联订单号（哪个订单）。 */
  orderNo: string | null;
  /** 登录阶段码（30 = 等邮箱码 / 32 = 等令牌码）。 */
  stage: number | null;
  /** 动作类型（哪类操作）。 */
  action: string | null;
  /** 具体失败码（`error_code` / `failure_code`）。 */
  code: string | null;
  /** 完整卡密（cdkey.bound 专属——2026-10-24 裁定：该帧收件人仅卡主）。 */
  cdkey: string | null;
}

/**
 * 事实槽位的**载体键名**——铃铛帧的载荷键与通知行读面的列名**同名**
 * ⇒ 补弹那路照抄即可（后端一个抽取点、两个载体）。
 *
 * ⚠️ **唯一真源**：跨端闸对着它校验后端的铃铛帧 schema
 * （`features/events/frameCatalogParity.test.ts`）⇒ 前端读一个后端没有的
 * 键 ⇒ **当场红**，不会等到界面上「少一行细节」。
 */
export const NOTICE_FACT_KEYS = {
  subject: 'subject',
  orderNo: 'order_no',
  stage: 'stage',
  action: 'action',
  code: 'code',
  cdkey: 'cdkey',
} as const;

/** 事实槽位的载体键名清单（闸要逐条对账 ⇒ 这里是那一份清单）。 */
export const NOTICE_FACT_SLOT_NAMES: readonly string[] = Object.values(NOTICE_FACT_KEYS);

/**
 * 事实的**正文行键**——「具体内容」的每一行各有一个键。
 *
 * ⚠️ **放在域层是为了让 i18n 闸看得见它们**：闸只扫 `t('字面量')` 与
 * `shared/domain/` 里的点号串 ⇒ 写在 feature 层的常量它**抓不到** ⇒ 键名写错
 * 了没人红 ⇒ 界面上印出原始键（i18next 对缺键返回键本身，不报错）。
 */
export const NOTICE_FACT_LINE_KEYS = {
  cdkey: 'notifications.cdkeyCode',
  account: 'notifications.subjectAccount',
  order: 'events.notify.factOrder',
  code: 'events.notify.factCode',
} as const;

/** 空事实——「什么都不知道」。 */
export const EMPTY_NOTICE_FACTS: NoticeFacts = {
  subject: null,
  orderNo: null,
  stage: null,
  action: null,
  code: null,
  cdkey: null,
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function int(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/**
 * 从载体里读事实——**铃铛帧与通知行读面同形**（后端一个抽取点、两个载体）。
 *
 * ⚠️ `username` 作 `subject` 的**回落**：通知行的 `username` 列**就是**主体
 * （落行时 `username=subject`）⇒ 补弹那路可以直接把行交给本函数。
 */
export function noticeFactsOf(source: unknown): NoticeFacts {
  const rec = asRecord(source);
  if (rec === null) return EMPTY_NOTICE_FACTS;
  return {
    subject: str(rec[NOTICE_FACT_KEYS.subject]) ?? str(rec['username']),
    orderNo: str(rec[NOTICE_FACT_KEYS.orderNo]),
    stage: int(rec[NOTICE_FACT_KEYS.stage]),
    action: str(rec[NOTICE_FACT_KEYS.action]),
    code: str(rec[NOTICE_FACT_KEYS.code]),
    cdkey: str(rec[NOTICE_FACT_KEYS.cdkey]),
  };
}

/** 标题——**键 + 分档**（无插值：标题是事件名，不带主体）。 */
export interface NoticeTitle {
  /** i18n 键。 */
  key: string;
  /** 分档标识——同档 ⇒ 同格（聚合键的一段）。 */
  grade: string;
  /**
   * 标题**已经说出**的那个业务码（`null` = 标题没说任何码）。
   *
   * ⚠️ **正文不重复印它**：等码档的标题「账号 alice 需要邮箱验证码」=
   * `guard_email_code` 的人话 ⇒ 正文再印一遍是废话。
   *
   * ⚠️ **但等码免疫那档要印**：携码遇错码（`guard_code_invalid`）⇒ 回落原等码档
   * ⇒ 标题说「需要邮箱验证码」，正文要补「验证码无效」——那才是操作员此刻
   * 真正缺的信息（`covered` ≠ 该码 ⇒ 不被这句标题覆盖）。
   */
  coveredCode: string | null;
}

/** 登录阶段 → 等码分档（30 = 邮箱码 / 32 = 令牌码）。 */
const NEED_CODE_GRADE_BY_STAGE: Record<number, string> = {
  30: 'email',
  32: 'token',
};

/**
 * 等码分档 → **标题键 + 标题已经说出的那个码**。
 *
 * ⚠️ `covered` 与标题键**同处一地** ⇒ 改了标题文案不必记得改别处
 * （判定跟着选标题一起走，见 ADR 0011 §4）。
 */
const NEED_CODE_BY_GRADE: Record<string, { key: string; covered: string | null }> = {
  email: { key: 'notifications.toast.account_need_code_email', covered: 'guard_email_code' },
  token: { key: 'notifications.toast.account_need_code_token', covered: 'guard_2fa_code' },
  code: { key: 'notifications.toast.account_need_code', covered: null },
};

/**
 * 动作**尾段** → 失败分档。
 *
 * ⚠️ 取 op 名的**尾段**（`account.keep_alive` → `keep_alive`）而不是全名：
 * 全名是**协议值**（点号串），在前端域层当键用会被 i18n 闸当成「缺键的
 * i18n 路径」⇒ 报假阳性。尾段在 9 个 op 里唯一 ⇒ 够用。
 */
const FAILED_GRADE_BY_ACTION: Record<string, string> = {
  login: 'login',
  relogin: 'relogin',
  verify: 'verify',
  token_refresh: 'token_refresh',
  keep_alive: 'keep_alive',
};

/** 失败分档 → 标题键。 */
const FAILED_TITLE_BY_GRADE: Record<string, string> = {
  login: 'notifications.toast.account_failed_login',
  relogin: 'notifications.toast.account_failed_relogin',
  verify: 'notifications.toast.account_failed_verify',
  token_refresh: 'notifications.toast.account_failed_token_refresh',
  keep_alive: 'notifications.toast.account_failed_keep_alive',
  failed: 'notifications.toast.account_failed',
};

/** 卡绑定 → 标题键。 */
const CDKEY_BOUND_TITLE = 'notifications.toast.cdkey_bound';

/** 动作尾段（`account.keep_alive` → `keep_alive`）。 */
function actionTail(action: string | null): string {
  if (action === null) return '';
  return action.slice(action.lastIndexOf('.') + 1);
}

/**
 * 来源帧 + 事实 ⇒ **事件名标题**。
 *
 * ⚠️ 标题只由**帧 + 分档**决定（stage / action）——主体进浮层正文，
 * 不进标题（用户裁定 2026-10-24）⇒ 主体缺失不影响标题。
 */
export function noticeTitle(frame: string, facts: NoticeFacts): NoticeTitle {
  if (frame === 'account.need_code') {
    const grade = NEED_CODE_GRADE_BY_STAGE[facts.stage ?? -1] ?? 'code';
    const picked = NEED_CODE_BY_GRADE[grade];
    return {
      key: picked?.key ?? frameKey(frame),
      grade,
      coveredCode: picked?.covered ?? null,
    };
  }
  if (frame === 'account.failed') {
    const grade = FAILED_GRADE_BY_ACTION[actionTail(facts.action)] ?? 'failed';
    return { key: FAILED_TITLE_BY_GRADE[grade] ?? frameKey(frame), grade, coveredCode: null };
  }
  if (frame === 'cdkey.bound') {
    return { key: CDKEY_BOUND_TITLE, grade: 'bound', coveredCode: null };
  }
  // 其余来源帧（含 `fx.refresh_failed`）：类别名**本身就是事件名**
  // （「汇率刷新失败」）⇒ 不另立一套文案。
  return { key: frameKey(frame), grade: 'plain', coveredCode: null };
}
